import React, { useState, useEffect } from "react";
import { Layout, Row, Col, Typography, Button, Switch, Divider, message } from "antd";
import {
  UnorderedListOutlined,
  ToolOutlined,
  CarOutlined,
  TagOutlined,
  FileProtectOutlined,
  SearchOutlined,
  ReadOutlined,
  BulbOutlined,
  BulbFilled
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import "./home.scss";
import TopBar from "../features/TopBar/TopBar";
import heroImage from "../assets/two-phone.png"; // Importing the image directly
import yousifImage from "../assets/yousif.jpg";

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;

// --- Data ---
const testimonials = [
  {
    quote: "Report showed frame damage in a photo. Seller swore it was never wrecked. Walked away.",
    name: "Sarah M.",
    location: "Portland"
  },
  {
    quote: "Showed the seller the report and negotiated $2,200 off because of missing maintenance. Worth the $5.",
    name: "Mike T.",
    location: "Austin"
  },
  {
    quote: "Warned me about the timing chain issue that kills these engines. That's like a $3,000 repair.",
    name: "Jessica R.",
    location: "Miami"
  }
];

const HomePage: React.FC = () => {
  // Default to light for this request, but toggleable
  const [theme, setTheme] = useState("light");

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  useEffect(() => {
    // Check for payment status from Stripe redirect
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment_status');
    if (paymentStatus) {
      if (paymentStatus === 'success') {
        message.success('Payment successful! Your credits will be updated shortly.');
      } else if (paymentStatus === 'cancel') {
        message.warning('Payment was cancelled.');
      }
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  return (
    <Layout className="landing-page">
      <TopBar className="title" title="" />

      <Content className="landing-content">

        {/* --- 1. HERO SECTION --- */}
        <div className="section-container hero-container">
          <div className="container-max">
            <Row align="middle" gutter={[64, 48]} style={{ height: '100%' }}>
              <Col xs={24} md={12} className="hero-text-col">
                <div className="hero-content-wrapper">
                  <div className="badge-tag">FIRST REPORT FREE</div>
                  <Title level={1}>A cheat sheet for any used-car listing.</Title>
                  <Paragraph className="hero-desc">
                    Paste a listing URL (or photos) → get what to pay, what’s likely wrong, and repair-cost ranges.
                    Total transparency a seller won’t give you.
                  </Paragraph>

                  <div className="hero-action-block">
                    <Row>
                      <Col xs={24} md={12}>
                        <Link to="/create-report">
                          <Button type="primary" size="large" className="btn-primary-green icon-btn-right">
                            Analyze a Vehicle
                          </Button>
                        </Link>
                      </Col>
                      <Col xs={24} md={12}>
                        <Link to="/vin">
                          <Button type="primary" size="large" className="btn-primary-blue icon-btn-right">
                            VIN Check Only
                          </Button>
                        </Link>
                      </Col>
                    </Row>
                    <Text className="subtext-small">
                      Works with <strong>most</strong> listing URLs or photos • First one free, then $5 per report • See what your car is worth
                    </Text>

                    <a href="https://crdemo.pacer.co/#/?docId=8b5e88c1-0f8c-4709-be1e-cdafc95ece8b&step=5" target="_blank" rel="noopener noreferrer" className="link-arrow">
                      See an example report &rarr;
                    </a>
                  </div>
                </div>
              </Col>
              <Col xs={24} md={12} className="hero-image-col">
                <img src={heroImage} alt="App Screenshot" className="hero-img-actual" />
              </Col>
            </Row>
          </div>
        </div>

        {/* --- 2. FEATURES (Dark Background) --- */}
        <div className="section-container bg-dark text-white features-container">
          <div className="container-max center-text">
            <Title level={2} style={{ color: 'white' }}>What you get</Title>
            <Text style={{ color: '#ccc' }}>Every report includes the same core checks. No guessing.</Text>

            <Row gutter={[32, 48]} style={{ marginTop: '60px', justifyContent: 'center' }}>
              <Col xs={24} md={8} lg={4} className="feature-card-dark">
                <div className="icon-box"><ToolOutlined /></div>
                <Title level={5} style={{ color: 'white' }} className="feature-title">Vehicle-specific common problems</Title>
                <Text className="feature-text">Identify and display vehicle-specific common problems, including their justification and likelihood.</Text>
              </Col>
              <Col xs={24} md={8} lg={4} className="feature-card-dark">
                <div className="icon-box"><UnorderedListOutlined /></div>
                <Title level={5} style={{ color: 'white' }} className="feature-title">Interior and exterior damage in photos</Title>
                <Text className="feature-text">Highlight damages, inconsistencies, and issues found in photos and descriptions.</Text>
              </Col>
              <Col xs={24} md={8} lg={4} className="feature-card-dark">
                <div className="icon-box"><CarOutlined /></div>
                <Title level={5} style={{ color: 'white' }} className="feature-title">Missing maintenance and open recalls</Title>
                <Text className="feature-text">No need to read the CarFax; we automatically flag missing maintenance and anomalies.</Text>
              </Col>
              <Col xs={24} md={8} lg={4} className="feature-card-dark">
                <div className="icon-box"><TagOutlined /></div>
                <Title level={5} style={{ color: 'white' }} className="feature-title">Market value & what you should pay</Title>
                <Text className="feature-text">Find out what impacts price and what to pay to get a deal.</Text>
              </Col>
              <Col xs={24} md={8} lg={4} className="feature-card-dark">
                <div className="icon-box"><FileProtectOutlined /></div>
                <Title level={5} style={{ color: 'white' }} className="feature-title">Free history report included</Title>
                <Text className="feature-text">VIN included? You get a free Basic Vehicle History report. Add Carfax if you want full coverage.</Text>
              </Col>
            </Row>
          </div>
        </div>

        {/* --- 3. SELECTION (Two Ways) --- */}
        <div className="section-container selection-section">
          <div className="container-max center-text">
            <Title level={2}>Two ways to get answers</Title>
            <Text>Deep-dive a listing, or research a model before you shop.</Text>

            <Row gutter={[32, 32]} style={{ marginTop: '50px' }} justify="center">
              <Col xs={24} md={10}>
                <div className="choice-card bordered">
                  <div className="card-header">
                    <SearchOutlined className="big-icon" />
                    <Title level={3}>Analyze a listing</Title>
                    <Paragraph>Use a listing URL or upload your own photos to get pricing, repairs, and red flags.</Paragraph>
                  </div>
                  <div className="card-footer">
                    <Link to="/create-report">
                      <Button block size="large" className="btn-primary-green">Analyze a Listing</Button>
                    </Link>
                    <Text className="tiny-text">First one free, then $5 per report</Text>
                  </div>
                </div>
              </Col>
              <Col xs={24} md={10}>
                <div className="choice-card bordered">
                  <div className="card-header">
                    <ReadOutlined className="big-icon" />
                    <Title level={3}>Research a model</Title>
                    <Paragraph>Still shopping? See common issues, trims to avoid, and what to check in listings.</Paragraph>
                  </div>
                  <div className="card-footer">
                    <Link to="/research">
                      <Button block size="large" className="btn-primary-green">Research a Model</Button>
                    </Link>
                    <Text className="tiny-text">Always free</Text>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </div>

        {/* --- 4. DOWNSIDE PROTECTION (Dark) --- */}
        <div className="section-container bg-dark text-white">
          <div className="container-max center-text">
            <Title level={2} style={{ color: 'white', marginBottom: '10px' }}>$5 won’t even cover lunch.</Title>
            <Title level={3} style={{ color: '#ccc', marginTop: 0, fontWeight: 400 }}>But it will cover your downside.</Title>

            <div className="checklist-wrapper-center">
              <div className="checklist-vertical">
                <div className="check-item"><span className="bar-accent">|</span> Model-specific failure points (and what they cost)</div>
                <div className="check-item"><span className="bar-accent">|</span> Photo red flags (damage sellers don’t mention)</div>
                <div className="check-item"><span className="bar-accent">|</span> Fair offer range (so you don’t overpay)</div>
                <div className="check-item"><span className="bar-accent">|</span> Follow-up & negotiation ammo (what to ask and point out)</div>
              </div>
            </div>
          </div>
        </div>

        {/* --- 5. FOUNDER STORY (Light) --- */}
        <div className="section-container founder-section">
          <div className="container-max">
            <div className="center-text" style={{ marginBottom: '60px' }}>
              <Title level={2}>How this started</Title>
            </div>
            <Row gutter={[48, 48]} align="top">
              <Col xs={24} md={10}>
                <img src={yousifImage} alt="Founder" className="founder-img-placeholder" /><br />
                <Text italic style={{ fontSize: '12px', opacity: 0.7 }}>One of the 100. Each one taught me what to look for.</Text>

              </Col>
              <Col xs={24} md={14}>
                <div className="story-content">
                  <Paragraph className="story-text">
                    August 2025. I had to move from California to NYC with a project car. U-Haul wanted $5,300.
                  </Paragraph>
                  <Paragraph className="story-text">
                    I've owned 100+ cars—but I knew Miatas, not trucks. So I built a listing analyzer.
                  </Paragraph>
                  <Paragraph className="story-text">
                    Found a 2001 GMC Sierra for $5,300. The tool flagged a center support bearing issue. I ignored it. (Regretted this.)
                  </Paragraph>
                  <Paragraph className="story-text">
                    Drove 3,000 miles. Bearing started disintegrating in New Mexico.
                  </Paragraph>
                  <Title level={3} className="story-highlight-title">Sold it in Philly for $6,800.</Title>
                  <Paragraph className="story-text">
                    Made $1,500 profit moving across the country because I knew exactly what I was buying and what it was worth.
                  </Paragraph>

                  <div className="yellow-highlight-box">
                    <strong>Here’s what hit me:</strong>
                    If this worked for a car guy buying outside his expertise, what would it do for regular people?
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </div>

        {/* --- 6. TESTIMONIALS (Dark) --- */}
        <div className="section-container bg-dark text-white">
          <div className="container-max">
            <Title level={2} className="center-text" style={{ color: 'white', marginBottom: '60px' }}>People dodging bullets</Title>
            <Row gutter={[32, 32]}>
              {testimonials.map((t, i) => (
                <Col xs={24} md={8} key={i}>
                  <div className="testimonial-card">
                    <Paragraph className="quote-text">"{t.quote}"</Paragraph>
                    <div className="quote-author">
                      - {t.name}, {t.location}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </div>

        {/* --- 7. CARFAX COMPARISON (Light) --- */}
        <div className="section-container carfax-section">
          <div className="container-max center-text" style={{ maxWidth: '900px' }}>
            <Title level={2}>Carfax helps. It’s not the whole picture.</Title>
            <Paragraph style={{ textAlign: 'left', marginTop: '30px' }}>It's more data.</Paragraph>
            <Paragraph style={{ textAlign: 'left' }}>
              Carfax will tell you about reported accidents, cataloged maintenance, and ownership history.
            </Paragraph>
            <Paragraph style={{ textAlign: 'left', fontWeight: 'bold' }}>
              It won't tell you:
            </Paragraph>
            <ul className="custom-ul">
              <li>The transmission is known to fail at 85k miles</li>
              <li>There's $2,400 in deferred maintenance</li>
              <li>The paint on the rear quarter panel is 2 shades off</li>
              <li>Similar cars in your area are selling for $3k less</li>
            </ul>

            <div className="yellow-highlight-box large-box" style={{ textAlign: 'left', alignItems: 'flex-start' }}>
              <strong>Clean Carfax ≠ Good Car</strong><br />
              That's why every listing analysis includes a free Basic History Report, with the option to add Carfax for complete history coverage.
            </div>
          </div>
        </div>

        {/* --- 8. BOTTOM CTA (Green Band) --- */}
        <div className="section-container bg-green-solid">
          <div className="container-max center-text">
            <Title level={2} style={{ color: 'white', marginBottom: '10px' }}>Stop gambling on used cars.</Title>
            <Text style={{ color: 'white', opacity: 0.9, display: 'block', marginBottom: '30px', fontSize: '1.2em' }}>
              Paste a listing. Get the truth. Know what you're buying.
            </Text>
            <Link to="/create-report">
              <Button size="large" className="btn-yellow-cta">
                Get Your Free Report &rarr;
              </Button>
            </Link>
            <Text style={{ display: 'block', fontSize: '12px', color: 'white', opacity: 0.6, marginTop: '10px' }}>
              No credit card needed for your first free report.
            </Text>
          </div>
        </div>

        {/* --- FOOTER --- */}
        <footer className="site-footer bg-dark text-white" style={{ padding: '60px 0 30px' }}>
          <div className="container-max">
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <Title level={5} style={{ color: 'white', margin: 0 }}>KickTheTires.ai</Title>
              <Text style={{ color: '#666', fontSize: '14px' }}>Built by a guy who's owned 100 cars and hates watching people get ripped off.</Text>
            </div>

            {/* Policy Links */}
            <div className="footer-links-row" style={{ display: 'flex', justifyContent: 'center', gap: '24px', marginBottom: '20px' }}>
              <Link to="/privacy-policy" className="footer-link">Privacy Policy</Link>
              <Link to="/terms-of-service" className="footer-link">Terms of Service</Link>
              <Link to="/cookie-policy" className="footer-link">Cookie Policy</Link>
            </div>

            <Divider style={{ borderColor: '#333' }} />

            <div className="footer-bottom" style={{ display: 'flex', justifyContent: 'center', position: 'relative' }}>
              <Text style={{ color: '#444', fontSize: '12px' }}>© 2026 KickTheTires.ai</Text>

              {/* Theme toggle */}
              <div style={{ position: 'absolute', right: 0, top: -5 }}>
                <Switch
                  checked={theme === "dark"}
                  onChange={toggleTheme}
                  checkedChildren={<BulbFilled />}
                  unCheckedChildren={<BulbOutlined />}
                  size="small"
                  style={{ opacity: 0.5 }}
                />
              </div>
            </div>
          </div>
        </footer>

      </Content>
    </Layout>
  );
};

export default HomePage;